import { formatDistanceToNow } from 'date-fns';
import React from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '@/lib/axios';
import { toast } from 'sonner';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Heart, MessageSquare, Share2, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { getInformativePostLabelClasses } from '@/lib/informative-post-label-styles';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

export interface InformativePost {
  id: string;
  label: string;
  content: string;
  image: string | null;
  startDate: string;
  endDate: string | null;
  createdAt: string;
  updatedAt: string;
  authorId: string;
  authorFirstName: string | null;
  authorLastName: string | null;
  authorImage: string | null;
  likeCount: number;
  commentCount: number;
  isLiked: boolean;
  communityId: string;
}

interface Comment {
  id: string;
  content: string;
  parentId: string | null;
  createdAt: string;
  author: {
    id: string;
    name: string;
    initials: string;
    image?: string | null;
  };
}

interface InformativePostCardProps {
  post: InformativePost;
  onPostUpdated?: () => void;
  communityId?: string;
}

export function InformativePostCard({
  post,
  onPostUpdated: _onPostUpdated,
  communityId,
}: InformativePostCardProps) {
  const [isLiked, setIsLiked] = React.useState(post.isLiked);
  const [likeCount, setLikeCount] = React.useState(post.likeCount);
  const [commentCount, setCommentCount] = React.useState(post.commentCount);
  const [showComments, setShowComments] = React.useState(false);

  const authorName = `${post.authorFirstName || ''} ${post.authorLastName || ''}`.trim() || 'Admin';
  const authorInitials = authorName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const timeAgo = formatDistanceToNow(new Date(post.createdAt), { addSuffix: true });

  React.useEffect(() => {
    setIsLiked(post.isLiked);
    setLikeCount(post.likeCount);
    setCommentCount(post.commentCount);
  }, [post.isLiked, post.likeCount, post.commentCount]);

  const handleLike = async () => {
    const previousLiked = isLiked;
    const previousCount = likeCount;

    setIsLiked(!previousLiked);
    setLikeCount(previousLiked ? previousCount - 1 : previousCount + 1);

    try {
      const response = await axiosInstance.post(`/api/posts/${post.id}/like`);
      setLikeCount(response.data.likeCount);
    } catch {
      setIsLiked(previousLiked);
      setLikeCount(previousCount);
      toast.error('Failed to update like');
    }
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/informative-post/${post.id}`;
    const shareData = {
      title: `${post.label}`,
      text: post.content,
      url: shareUrl,
    };

    try {
      await axiosInstance.post(`/api/posts/${post.id}/share`, { platform: 'internal' });

      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        toast.success('Shared successfully!');
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      }
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        toast.error('Failed to share');
      }
    }
  };

  return (
    <>
      <Card className="glass-card overflow-hidden rounded-2xl border-border/70 shadow-sm hover:shadow-md transition-shadow">
        <CardHeader className="flex flex-row items-start gap-4 p-4 pb-2">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <Link to={`/${communityId ? `${communityId}/` : ''}u/${post.authorId}`}>
              <Avatar className="w-10 h-10 border border-border hover:opacity-80 transition-opacity">
                <AvatarFallback>{authorInitials}</AvatarFallback>
              </Avatar>
            </Link>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Link
                  to={`/${communityId ? `${communityId}/` : ''}u/${post.authorId}`}
                  className="text-sm font-semibold text-foreground truncate hover:underline"
                >
                  {authorName}
                </Link>
                <Badge
                  variant="outline"
                  className={cn(
                    'text-xs font-medium shrink-0',
                    getInformativePostLabelClasses(post.label)
                  )}
                >
                  {post.label}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">{timeAgo}</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <div
            className="prose prose-sm dark:prose-invert max-w-none text-foreground prose-p:my-2 prose-a:text-primary prose-headings:text-foreground whitespace-pre-wrap"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
          {post.image && (
            <img
              src={post.image}
              alt=""
              className="mt-4 rounded-xl w-full max-h-64 object-cover border border-border/60"
            />
          )}
        </CardContent>
        <CardFooter className="p-2 border-t border-border bg-muted/25 dark:bg-muted/15 flex justify-between items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLike}
            className="flex-1 gap-2 text-muted-foreground hover:text-red-600 hover:bg-red-500/10 dark:hover:text-red-400 dark:hover:bg-red-500/15 transition-colors"
          >
            <Heart
              className={cn(
                'h-4 w-4',
                isLiked && 'fill-red-500 text-red-500 dark:fill-red-400 dark:text-red-400'
              )}
            />
            <span className="text-xs">{likeCount}</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowComments(true)}
            className="flex-1 gap-2 text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
          >
            <MessageSquare className="h-4 w-4" />
            <span className="text-xs">{commentCount}</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleShare}
            className="flex-1 gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <Share2 className="h-4 w-4" />
            <span className="text-xs">Share</span>
          </Button>
        </CardFooter>
      </Card>

      <InformativePostCommentsDialog
        postId={post.id}
        open={showComments}
        onOpenChange={setShowComments}
        onCommentAdded={() => setCommentCount((prev) => Number(prev) + 1)}
        communityId={communityId}
      />
    </>
  );
}

interface InformativePostCommentsDialogProps {
  postId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCommentAdded?: () => void;
  communityId?: string;
}

function InformativePostCommentsDialog({
  postId,
  open,
  onOpenChange,
  onCommentAdded,
  communityId,
}: InformativePostCommentsDialogProps) {
  const [comments, setComments] = React.useState<Comment[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [newComment, setNewComment] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const fetchComments = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/posts/${postId}/comments`);
      setComments(response.data);
    } catch {
      toast.error('Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [postId]);

  React.useEffect(() => {
    if (open) {
      fetchComments();
    }
  }, [open, fetchComments]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      await axiosInstance.post(`/api/posts/${postId}/comments`, {
        content: newComment,
      });
      await fetchComments();
      setNewComment('');
      onCommentAdded?.();
    } catch {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[80vh] flex flex-col border-border">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Comments</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {loading && comments.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary opacity-50" />
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-12 rounded-2xl border border-dashed border-border/70 bg-muted/20">
              <MessageSquare className="h-8 w-8 mx-auto mb-2 text-muted-foreground/70" />
              <p className="text-sm text-muted-foreground">
                No comments yet. Be the first to comment!
              </p>
            </div>
          ) : (
            <>
              {comments.map((comment) => (
                <div key={comment.id} className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Link to={`/${communityId ? `${communityId}/` : ''}u/${comment.author.id}`}>
                      <Avatar className="w-8 h-8 hover:opacity-80 transition-opacity">
                        {comment.author.image ? (
                          <AvatarImage src={comment.author.image} alt={comment.author.name} />
                        ) : null}
                        <AvatarFallback className="text-xs">
                          {comment.author.initials}
                        </AvatarFallback>
                      </Avatar>
                    </Link>
                    <div className="flex-1">
                      <div className="rounded-xl border border-border/60 bg-muted/40 dark:bg-muted/25 p-3 shadow-sm">
                        <Link
                          to={`/${communityId ? `${communityId}/` : ''}u/${comment.author.id}`}
                          className="text-sm font-semibold text-foreground hover:underline"
                        >
                          {comment.author.name}
                        </Link>
                        <p className="text-sm text-foreground mt-1 whitespace-pre-wrap">
                          {comment.content}
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 px-1">
                        {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
        <form onSubmit={handleSubmit} className="flex gap-2 pt-4 border-t border-border">
          <Input
            placeholder="Write a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            className="flex-1 bg-background"
          />
          <Button type="submit" disabled={submitting || !newComment.trim()}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
